import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * Independent Stays plan IS8: "Your data" — download everything, close the
 * account, reopen it (BE src/account/routes.js). Owner of a Stays-only account
 * only; everyone else gets a plain refusal. Refusals carry the server's message.
 */

export interface AccountExport {
  id: string;
  status: 'ready' | 'failed' | 'expired';
  bytes: number | null;
  files: number | null;
  created_at: string;
  expires_at: string;
  downloaded_at?: string | null;
}

export interface AccountClosureState {
  closure: { id: string; status: string; requested_at: string; freeze_until: string } | null;
  closing: boolean;
  can_reopen: boolean;
  can_close: boolean;
  grace_days: number;
}

export interface ClosePreview {
  confirm_with: string;
  grace_days: number;
  freeze_until: string;
  blockers: { code: string; count: number; link: string }[];
  effects: {
    people_signed_out: number;
    listings: number;
    properties: number;
    subscription_ends_at_period_end: boolean;
    period_end: string | null;
  };
}

export class AccountApiError extends Error {
  code: string;
  status: number;
  count: number | null;

  constructor(message: string, code: string, status: number, count: number | null) {
    super(message);
    this.code = code;
    this.status = status;
    this.count = count;
  }
}

const rethrow = (e: unknown): never => {
  const res = (e as AxiosError<{ message?: string; code?: string; count?: number }>).response;
  throw new AccountApiError(
    (res && res.data && res.data.message) || 'That did not go through. Nothing was changed. Please try again.',
    (res && res.data && res.data.code) || 'UNKNOWN',
    (res && res.status) || 0,
    res && res.data && typeof res.data.count === 'number' ? res.data.count : null
  );
};

const opts = { withCredentials: true };

export const listExports = (): Promise<AccountExport[]> =>
  axios
    .get<{ items: AccountExport[] }>(`${apiRoute}/account/exports`, opts)
    .then(r => r.data.items || [])
    .catch(rethrow);

export const createExport = (): Promise<AccountExport> =>
  axios
    .post<AccountExport>(`${apiRoute}/account/export`, {}, opts)
    .then(r => r.data)
    .catch(rethrow);

// A short-lived link to the zip (5 minutes). Absolute on S3; on the memory
// store a path under /api that this resolves against the API's origin.
export const exportLink = (id: string): Promise<string> =>
  axios
    .get<{ url: string }>(`${apiRoute}/account/export/${encodeURIComponent(id)}`, opts)
    .then(r =>
      /^https?:\/\//.test(r.data.url) ? r.data.url : `${String(apiRoute || '').replace(/\/api\/?$/, '')}${r.data.url}`
    )
    .catch(rethrow);

export const getClosure = (): Promise<AccountClosureState> =>
  axios
    .get<AccountClosureState>(`${apiRoute}/account/closure`, opts)
    .then(r => r.data)
    .catch(rethrow);

export const previewClose = (): Promise<ClosePreview> =>
  axios
    .post<ClosePreview>(`${apiRoute}/account/close/preview`, {}, opts)
    .then(r => r.data)
    .catch(rethrow);

export const closeAccount = (confirm: string): Promise<{ status: string; freeze_until: string | null }> =>
  axios
    .post<{ status: string; freeze_until: string | null }>(`${apiRoute}/account/close`, { confirm }, opts)
    .then(r => r.data)
    .catch(rethrow);

export const reopenAccount = (): Promise<{ status: string; billing_resumed: boolean }> =>
  axios
    .post<{ status: string; billing_resumed: boolean }>(`${apiRoute}/account/reopen`, {}, opts)
    .then(r => r.data)
    .catch(rethrow);
